import React from 'react';
import { Heart, Sparkles, Laugh, Gift } from 'lucide-react';

export const FourMore = () => {
  const fourMoreData = [
    {
      id: 1,
      title: "LIVE MORE",
      icon: Sparkles,
      description: "Embrace every moment with purpose and passion. Break free from limitations and design a life that reflects your highest potential.",
      principles: [
        "Pursue your boldest dreams",
        "Take inspired action daily", 
        "Create meaningful experiences"
      ]
    },
    {
      id: 2,
      title: "LOVE MORE",
      icon: Heart,
      description: "Lead with compassion and connection. Love yourself fiercely, love others deeply, and let love guide your transformation.",
      principles: [
        "Practice radical self-love",
        "Build authentic relationships",
        "Spread kindness unconditionally"
      ]
    },
    {
      id: 3,
      title: "LAUGH MORE",
      icon: Laugh,
      description: "Find joy in the journey. Resilience grows through laughter, and perspective shifts when we embrace lightness alongside depth.",
      principles: [
        "Celebrate small victories",
        "Choose joy over worry",
        "Share moments of levity"
      ]
    },
    {
      id: 4,
      title: "GIVE MORE",
      icon: Gift,
      description: "Your legacy is built through generosity. Give your time, talent, wisdom, and resources to elevate others and create lasting impact.",
      principles: [
        "Mentor and inspire others",
        "Contribute to your community",
        "Leave the world better"
      ]
    }
  ];

  return (
    <section id="four-more" className="relative py-24 bg-gunmetal-900">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 noise-texture"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
            <span className="text-platinum animate-chrome-pulse block mb-4">THE FOUR MORE</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-8"></div>
          <p className="text-2xl md:text-3xl text-steel-200 max-w-4xl mx-auto leading-relaxed mb-4">
            Our Core Campaign & Foundation
          </p>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto leading-relaxed">
            These four principles form the bedrock of MINDESIGN ENTERPRISES. 
            They're not just words—they're a commitment to living boldly, 
            loving deeply, laughing freely, and giving generously.
          </p>
        </div>

        {/* Four More Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {fourMoreData.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div 
                key={item.id}
                className="group relative bg-gradient-to-br from-gunmetal-800/70 to-gunmetal-950/70 backdrop-blur-sm rounded-lg p-8 md:p-10 border-2 border-steel-700/30 hover:border-accent-red/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20 overflow-hidden"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent-red/5 rounded-bl-full transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-500"></div>
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon & Title */}
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="p-4 bg-accent-red/10 rounded-lg group-hover:bg-accent-red/20 transition-colors duration-300">
                      <IconComponent className="text-accent-red" size={48} />
                    </div>
                    <h3 className="text-4xl md:text-5xl font-black text-diamond">
                      {item.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-steel-300 text-lg mb-6 leading-relaxed">
                    {item.description}
                  </p>

                  {/* Principles */}
                  <div className="space-y-3">
                    <p className="text-sm font-bold text-accent-red uppercase tracking-wider mb-3">
                      Key Principles:
                    </p>
                    {item.principles.map((principle, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-accent-red rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-steel-400">{principle}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Number indicator */}
                <div className="absolute bottom-6 right-6 text-8xl font-black text-steel-800/30 group-hover:text-steel-700/30 transition-colors duration-300">
                  0{item.id}
                </div>
              </div>
            );
          })}
        </div>

        {/* Campaign Statement */}
        <div className="bg-gunmetal-950/90 backdrop-blur-sm rounded-lg p-10 md:p-16 border-l-4 border-accent-red relative overflow-hidden">
          {/* Background accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-accent-red/5 to-transparent"></div>
          
          <div className="relative z-10">
            <blockquote className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-8">
              <span className="text-diamond animate-chrome-pulse">
                LIVE MORE. LOVE MORE. LAUGH MORE. GIVE MORE.
              </span>
            </blockquote>
            <p className="text-steel-300 text-xl md:text-2xl leading-relaxed mb-6">
              This isn't just our motto—it's our promise to you. Every book we publish, 
              every song we create, every piece of wisdom we share is designed to help 
              you embody these four principles in your daily life.
            </p>
            <p className="text-accent-red text-2xl font-bold italic">
              Your Life, Redesigned.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
